import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, Form, Input, Select, DatePicker, Button, message } from 'antd';
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Box, Flex } from 'atoms';
import { searchVendorV2 } from 'store/vendorSliceV2';

const { Option } = Select;
const { TextArea } = Input;

const StyledModal = styled(Modal)`
	.ant-modal-header {
		border-bottom: 1px solid #e0e0e0;
		padding: 16px 24px;
	}

	.ant-modal-title {
		font-size: 18px;
		font-weight: 600;
		color: #1c1c1c;
	}

	.ant-modal-body {
		padding: 24px;
	}

	.ant-modal-footer {
		border-top: 1px solid #e0e0e0;
		padding: 16px 24px;
		display: flex;
		justify-content: flex-end;
		gap: 12px;
	}
`;

const FormRow = styled(Flex)`
	margin-bottom: 24px;
	gap: 24px;

	&:last-of-type {
		margin-bottom: 0;
	}
`;

const FormItem = styled(Box)`
	flex: 1;

	.ant-form-item-label {
		padding-bottom: 8px;

		label {
			font-size: 13px;
			font-weight: 500;
			color: #333;

			&::before {
				display: none !important;
			}
		}

		label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::after {
			display: inline-block;
			margin-left: 4px;
			color: #ff4d4f;
			font-size: 13px;
			line-height: 1;
			content: '*';
		}
	}

	.ant-input,
	.ant-select-selector,
	.ant-picker {
		border-radius: 4px;
		height: 40px;
		font-size: 13px;
	}

	.ant-input:disabled,
	.ant-input[disabled],
	.ant-select-disabled .ant-select-selector {
		background-color: #f5f5f5;
		color: #666;
		cursor: not-allowed;
	}
`;

const CancelButton = styled(Button)`
	height: 40px;
	padding: 0 32px;
	font-weight: 500;
	border-radius: 4px;
	border-color: #ff4d4f;
	color: #ff4d4f;
	background: white;

	&:hover {
		border-color: #ff7875;
		color: #ff7875;
		background: white;
	}
`;

const ConfirmButton = styled(Button)`
	background: #18405d;
	border-color: #18405d;
	color: #fff;
	height: 40px;
	padding: 0 32px;
	font-weight: 500;
	border-radius: 4px;

	&:hover {
		background: #1e4a6b;
		border-color: #1e4a6b;
		color: #fff;
	}
`;

export default function TransferDriverModal({
	visible,
	onCancel,
	onConfirm,
	driverData,
	loading = false,
}) {
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const [vendorOptions, setVendorOptions] = useState([]);
	const [submitting, setSubmitting] = useState(false);

	// Fetch vendor options (excluding current vendor)
	useEffect(() => {
		if (visible) {
			dispatch(
				searchVendorV2({
					filter_by: { is_active: true },
					pagination: { page: 1, per_page: 1000 },
				})
			).then((response) => {
				if (response?.rows) {
					// Filter out current vendor
					const options = response.rows
						.filter((vendor) => vendor.id !== driverData?.vendor_id)
						.map((vendor) => ({
							value: vendor.id,
							label: vendor.vendor_name || vendor.name || `Vendor ${vendor.id}`,
						}));
					setVendorOptions(options);
				}
			});
		}
	}, [visible, dispatch, driverData?.vendor_id]);

	// Reset form when modal opens/closes or driver data changes
	useEffect(() => {
		if (visible && driverData) {
			form.setFieldsValue({
				driver_name: driverData.driver_name || '',
				current_vendor: driverData.vendor_name || '',
				to_vendor_id: undefined,
				reason: '',
				last_working_date: null,
				start_date: null,
			});
		} else if (!visible) {
			form.resetFields();
		}
	}, [visible, driverData, form]);

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields([
				'to_vendor_id',
				'reason',
				'last_working_date',
				'start_date',
			]);

			// Validate that start_date >= last_working_date
			if (values.start_date && values.last_working_date) {
				const startDate = dayjs(values.start_date);
				const lastDate = dayjs(values.last_working_date);

				if (startDate.isBefore(lastDate)) {
					message.error('Start date must be greater than or equal to last working date');
					return;
				}
			}

			const payload = {
				driver_id: driverData.id,
				to_vendor_id: values.to_vendor_id,
				reason: values.reason,
				last_working_date: values.last_working_date
					? dayjs(values.last_working_date).toISOString()
					: null,
				start_date: values.start_date ? dayjs(values.start_date).toISOString() : null,
			};

			setSubmitting(true);
			await onConfirm(payload);
			setSubmitting(false);
		} catch (error) {
			console.error('Form validation error:', error);
			setSubmitting(false);
		}
	};

	const handleCancel = () => {
		form.resetFields();
		onCancel();
	};

	return (
		<StyledModal
			title="Transfer Driver"
			open={visible}
			onCancel={handleCancel}
			footer={[
				<CancelButton key="cancel" onClick={handleCancel} disabled={submitting}>
					Cancel
				</CancelButton>,
				<ConfirmButton key="confirm" onClick={handleSubmit} loading={submitting || loading}>
					Confirm
				</ConfirmButton>,
			]}
			width={900}
			centered
			maskClosable={false}
		>
			<Form form={form} layout="vertical">
				<FormRow>
					<FormItem>
						<Form.Item label="Driver Name" name="driver_name">
							<Input size="large" disabled />
						</Form.Item>
					</FormItem>
					<FormItem>
						<Form.Item label="Current Vendor" name="current_vendor">
							<Input size="large" disabled />
						</Form.Item>
					</FormItem>
				</FormRow>

				<FormRow>
					<FormItem>
						<Form.Item
							label="Transfer to Vendor"
							name="to_vendor_id"
							rules={[
								{ required: true, message: 'Please select vendor to transfer to' },
							]}
						>
							<Select placeholder="Select" size="large" showSearch>
								{vendorOptions.map((vendor) => (
									<Option key={vendor.value} value={vendor.value}>
										{vendor.label}
									</Option>
								))}
							</Select>
						</Form.Item>
					</FormItem>
					<FormItem>
						<Form.Item
							label="Reason for Transfer"
							name="reason"
							rules={[
								{ required: true, message: 'Please enter reason for transfer' },
								{ max: 255, message: 'Reason must be less than 255 characters' },
							]}
						>
							<TextArea
								placeholder="Enter reason for transfer"
								size="large"
								rows={3}
								maxLength={255}
								showCount
							/>
						</Form.Item>
					</FormItem>
				</FormRow>

				<FormRow>
					<FormItem>
						<Form.Item
							label="Last Date of working with Previous Vendor"
							name="last_working_date"
							rules={[
								{
									required: true,
									message: 'Please select last working date',
								},
							]}
						>
							<DatePicker
								placeholder="Choose Date"
								size="large"
								style={{ width: '100%' }}
								format="DD-MM-YYYY"
							/>
						</Form.Item>
					</FormItem>
					<FormItem>
						<Form.Item
							label="Start Date of working with New Vendor"
							name="start_date"
							rules={[
								{
									required: true,
									message: 'Please select start date',
								},
								({ getFieldValue }) => ({
									validator(_, value) {
										const lastDate = getFieldValue('last_working_date');
										if (!value || !lastDate) {
											return Promise.resolve();
										}
										if (dayjs(value).isBefore(dayjs(lastDate))) {
											return Promise.reject(
												new Error(
													'Start date must be greater than or equal to last working date'
												)
											);
										}
										return Promise.resolve();
									},
								}),
							]}
						>
							<DatePicker
								placeholder="Choose Date"
								size="large"
								style={{ width: '100%' }}
								format="DD-MM-YYYY"
								disabledDate={(current) => {
									const lastDate = form.getFieldValue('last_working_date');
									if (lastDate) {
										return current && current.isBefore(dayjs(lastDate), 'day');
									}
									return false;
								}}
							/>
						</Form.Item>
					</FormItem>
				</FormRow>
			</Form>
		</StyledModal>
	);
}

TransferDriverModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	onCancel: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	driverData: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
		driver_name: PropTypes.string,
		vendor_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		vendor_name: PropTypes.string,
	}),
	loading: PropTypes.bool,
};
